import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiStar, FiPlus, FiTrash2, FiX, FiImage, FiUser, FiType, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import Sidebar from '../../components/Admin/Sidebar';
import SafeIcon from '../../common/SafeIcon';
import ncb from '../../db/ncb';

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const [formData, setFormData] = useState({
    guest_name: '',
    role: '',
    content: '',
    rating: 5,
    image_url: '',
    is_featured: true
  });

  const fetchReviews = () => {
    setLoading(true);
    const data = ncb.reviews.list();
    setReviews(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      ncb.reviews.add(formData);
      showNotification('Review added to backend');
      setIsModalOpen(false);
      setFormData({ guest_name: '', role: '', content: '', rating: 5, image_url: '', is_featured: true });
      fetchReviews();
    } catch (err) {
      showNotification('Error saving review', 'error');
    }
    setLoading(false);
  };

  const deleteReview = (id) => {
    if (window.confirm('Are you sure you want to delete this review?')) {
      ncb.reviews.delete(id);
      showNotification('Review removed');
      fetchReviews();
    }
  };

  return (
    <div className="flex bg-brand-cream min-h-screen">
      <Sidebar />
      <main className="flex-grow p-8">
        <header className="mb-10 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-3xl text-brand-dark font-bold">Guest Reviews</h1>
            <p className="text-brand-sage text-sm">Manage and feature testimonials from your guests.</p>
          </div>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-brand-olive text-white px-6 py-3 rounded-sm flex items-center gap-2 uppercase tracking-widest text-xs font-bold hover:bg-brand-dark transition-all shadow-lg active:scale-95"
          >
            <SafeIcon icon={FiPlus} /> Add New Review
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={review.id}
              className="bg-white p-6 rounded-sm border border-brand-light/30 shadow-sm relative group hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-brand-light bg-brand-cream">
                    <img src={review.image_url || 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?q=80&w=100&auto=format&fit=crop'} className="w-full h-full object-cover" alt={review.guest_name} />
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-dark text-sm uppercase tracking-tight">{review.guest_name}</h3>
                    <p className="text-[10px] text-brand-sage uppercase tracking-widest">{review.role || 'Guest'}</p>
                  </div>
                </div>
                <div className="flex gap-0.5 text-amber-500">
                  {[...Array(review.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="text-[10px] fill-current" />
                  ))}
                </div>
              </div>
              <p className="text-brand-sage text-sm font-light italic leading-relaxed mb-6 line-clamp-4">
                "{review.content}"
              </p>
              <div className="flex justify-between items-center pt-4 border-t border-brand-light/20">
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold ${review.is_featured ? 'bg-green-100 text-green-700' : 'bg-brand-cream text-brand-sage'}`}>
                  {review.is_featured ? 'Featured' : 'Hidden'}
                </span>
                <button 
                  onClick={() => deleteReview(review.id)}
                  className="text-brand-light hover:text-red-500 transition-colors p-2"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal Form */}
        <AnimatePresence>
          {isModalOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
              <motion.div 
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="relative bg-white w-full max-w-xl rounded-sm shadow-2xl p-8 overflow-y-auto max-h-[90vh]"
              >
                <div className="flex justify-between items-center mb-8">
                  <h2 className="font-serif text-2xl text-brand-dark">New Guest Review</h2>
                  <button onClick={() => setIsModalOpen(false)} className="text-brand-sage hover:text-brand-dark">
                    <SafeIcon icon={FiX} className="text-2xl" />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Guest Name</label>
                      <div className="relative">
                        <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light" />
                        <input type="text" required className="w-full pl-10 pr-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm" value={formData.guest_name} onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })} placeholder="e.g. Maria Santos" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Role (Optional)</label>
                      <div className="relative">
                        <SafeIcon icon={FiType} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light" />
                        <input type="text" className="w-full pl-10 pr-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} placeholder="e.g. Family Traveler" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Review Content</label>
                    <textarea required rows="4" className="w-full p-4 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm resize-none" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder="Paste testimonial here..." />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Avatar URL</label>
                    <div className="relative">
                      <SafeIcon icon={FiImage} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light" />
                      <input type="url" className="w-full pl-10 pr-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm" value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="Link to photo" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Rating</label>
                      <select className="w-full p-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none text-sm appearance-none cursor-pointer" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}>
                        {[5, 4, 3, 2, 1].map(num => <option key={num} value={num}>{num} Stars</option>)}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center gap-3 cursor-pointer p-3 bg-brand-cream/30 border border-brand-light/50 w-full group">
                        <input type="checkbox" className="w-4 h-4 accent-brand-olive cursor-pointer" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} />
                        <span className="text-[10px] uppercase tracking-widest text-brand-dark font-bold">Feature on Website</span>
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="w-full bg-brand-olive text-white py-4 uppercase tracking-[0.2em] font-bold hover:bg-brand-dark transition-all shadow-lg active:scale-[0.98]">
                    {loading ? 'Saving...' : 'Save Review'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Notifications */}
        <AnimatePresence>
          {notification && (
            <motion.div initial={{ opacity: 0, y: 50, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 50, x: '-50%' }} className={`fixed bottom-8 left-1/2 z-[200] px-6 py-3 rounded-sm shadow-2xl flex items-center gap-3 text-white text-sm font-bold uppercase tracking-widest ${notification.type === 'error' ? 'bg-red-500' : 'bg-brand-olive'}`}>
              <SafeIcon icon={notification.type === 'error' ? FiAlertCircle : FiCheckCircle} />
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ReviewsManagement;